import { toast } from 'react-hot-toast';

/**
 * Optimistic notification system that shows success immediately,
 * then only shows error if the operation fails.
 * 
 * This replaces the pattern of showing loading -> success/error
 * with immediate success -> error only if needed
 */

class OptimisticNotificationManager {
  constructor() {
    this.activeNotifications = new Map();
  }

  /**
   * Show optimistic success notification and track the promise
   * @param {string} successMessage - Message to show immediately
   * @param {Promise} promise - The async operation promise
   * @param {string} errorMessage - Message to show if operation fails
   * @param {Object} options - Additional options
   * @returns {Promise} - The original promise
   */
  async optimistic(successMessage, promise, errorMessage, options = {}) {
    const {
      id = null,
      duration = 4000,
      onSuccess = null,
      onError = null,
      revertAction = null // Function to revert optimistic changes
    } = options;

    // Show immediate success
    const toastId = id || `optimistic-${Date.now()}-${Math.random()}`;
    toast.success(successMessage, { 
      id: toastId, 
      duration 
    });

    // Track this notification
    this.activeNotifications.set(toastId, {
      promise,
      errorMessage,
      revertAction,
      successMessage
    });

    try {
      const result = await promise;
      
      // Operation succeeded, remove from tracking
      this.activeNotifications.delete(toastId);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      // Operation failed, show error and revert if needed
      this.activeNotifications.delete(toastId);
      
      // Revert optimistic changes if revert function provided
      if (revertAction) {
        try {
          revertAction();
        } catch (revertError) {
          console.error('Error reverting optimistic change:', revertError);
        }
      }
      
      // Show error message
      toast.error(errorMessage, { 
        id: toastId,
        duration: 6000 // Errors stay longer
      });
      
      // Call error callback if provided
      if (onError) {
        onError(error);
      }
      
      // Re-throw to maintain promise chain
      throw error;
    }
  }

  /**
   * Batch optimistic operations - show success for all, then only show errors for failed ones
   * @param {Array} operations - Array of {successMessage, promise, errorMessage, options}
   * @returns {Promise} - Promise.allSettled result
   */
  async batch(operations) {
    const toastIds = [];
    
    // Show all success messages immediately
    operations.forEach((op, index) => {
      const toastId = op.options?.id || `batch-${Date.now()}-${index}`;
      toast.success(op.successMessage, { 
        id: toastId, 
        duration: op.options?.duration || 4000 
      });
      toastIds.push(toastId);
    });

    // Execute all operations
    const results = await Promise.allSettled(
      operations.map(op => op.promise)
    );

    // Handle results
    results.forEach((result, index) => {
      const operation = operations[index];
      const toastId = toastIds[index];

      if (result.status === 'rejected') {
        // Operation failed, revert and show error
        if (operation.options?.revertAction) {
          try {
            operation.options.revertAction();
          } catch (revertError) {
            console.error('Error reverting optimistic change:', revertError);
          }
        }

        toast.error(operation.errorMessage, { 
          id: toastId,
          duration: 6000 
        });

        if (operation.options?.onError) {
          operation.options.onError(result.reason);
        }
      } else {
        // Operation succeeded
        if (operation.options?.onSuccess) {
          operation.options.onSuccess(result.value);
        }
      }
    });

    return results;
  }

  /**
   * Cancel an optimistic notification (useful if component unmounts)
   * @param {string} id - The notification ID
   */
  cancel(id) {
    if (this.activeNotifications.has(id)) {
      this.activeNotifications.delete(id);
      toast.dismiss(id);
    }
  }

  /**
   * Cancel all active optimistic notifications
   */
  cancelAll() {
    this.activeNotifications.forEach((_, id) => {
      toast.dismiss(id);
    });
    this.activeNotifications.clear();
  }
}

// Create singleton instance
const optimisticNotifications = new OptimisticNotificationManager();

// Convenience functions for common patterns
export const optimistic = {
  /**
   * Standard optimistic notification
   */
  success: (successMessage, promise, errorMessage, options) => 
    optimisticNotifications.optimistic(successMessage, promise, errorMessage, options),

  /**
   * Batch operations
   */
  batch: (operations) => 
    optimisticNotifications.batch(operations),

  /**
   * Create operation - "Created successfully" -> "Failed to create"
   */
  create: (itemName, promise, options = {}) =>
    optimisticNotifications.optimistic(
      `${itemName} created successfully!`,
      promise,
      `Failed to create ${itemName.toLowerCase()}. Please try again.`,
      options
    ),

  /**
   * Update operation - "Updated successfully" -> "Failed to update"
   */
  update: (itemName, promise, options = {}) =>
    optimisticNotifications.optimistic(
      `${itemName} updated successfully!`,
      promise,
      `Failed to update ${itemName.toLowerCase()}. Please try again.`,
      options
    ),

  /**
   * Delete operation - "Deleted successfully" -> "Failed to delete"
   */
  delete: (itemName, promise, options = {}) =>
    optimisticNotifications.optimistic(
      `${itemName} deleted successfully!`,
      promise,
      `Failed to delete ${itemName.toLowerCase()}. Please try again.`,
      options
    ),

  /**
   * Cancel notification
   */
  cancel: (id) => optimisticNotifications.cancel(id),

  /**
   * Cancel all notifications
   */
  cancelAll: () => optimisticNotifications.cancelAll(),

  /**
   * Info notification - simple info message
   */
  info: (message, options = {}) => {
    const { duration = 4000 } = options;
    return toast(message, { duration });
  }
};

export default optimistic;